import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  ScrollView,
} from '../utils/webComponents';
import { useNavigation } from '../utils/navigation';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../hooks/useTranslation';
import LanguageModal from '../components/LanguageModal';
import {
  Settings,
  Language,
  DarkMode,
  LightMode,
  PrivacyTip,
  Description,
  Help,
  Info,
  ChevronRight,
  ArrowBack,
} from '@mui/icons-material';

const SettingsScreen = () => {
  const navigation = useNavigation();
  const { theme, isDarkMode, toggleTheme } = useTheme();
  const { language, getCurrentLanguageName } = useLanguage();
  const { t } = useTranslation();
  const [languageModalVisible, setLanguageModalVisible] = useState(false);

  const handleNavigate = (screen) => {
    navigation.navigate(screen);
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <ArrowBack sx={{ fontSize: 24, color: theme.text }} />
          </TouchableOpacity>
          <Text style={[styles.title, { color: theme.text }]}>{t('settings.title')}</Text>
          <View style={{ width: 40 }} /> {/* Spacer for centering */}
        </View>

        {/* Display Settings */}
        <View style={[styles.card, { backgroundColor: theme.surface, borderColor: theme.border }]}>
          <Text style={[styles.sectionTitle, { color: theme.text }]}>
            {t('settings.displaySettings')}
          </Text>

          {/* Language Setting */}
          <TouchableOpacity
            style={[styles.settingItem, styles.firstSettingItem, { borderBottomColor: theme.border }]}
            onPress={() => setLanguageModalVisible(true)}
          >
            <View style={styles.settingLeft}>
              <View style={[styles.settingIcon, { backgroundColor: theme.primaryLight }]}>
                <Language sx={{ fontSize: 20, color: theme.primary }} />
              </View>
              <Text style={[styles.settingLabel, { color: theme.text }]}>
                {t('settings.language')}
              </Text>
            </View>
            <ChevronRight sx={{ fontSize: 20, color: theme.textMuted }} />
          </TouchableOpacity>

          {/* Dark Mode Toggle */}
          <View style={styles.settingItem}>
            <View style={styles.settingLeft}>
              <View style={[styles.settingIcon, { backgroundColor: theme.primaryLight }]}>
                {isDarkMode ? (
                  <DarkMode sx={{ fontSize: 20, color: theme.primary }} />
                ) : (
                  <LightMode sx={{ fontSize: 20, color: theme.primary }} />
                )}
              </View>
              <Text style={[styles.settingLabel, { color: theme.text }]}>
                {t('settings.darkMode')}
              </Text>
            </View>
            <TouchableOpacity
              style={[
                styles.toggle,
                { backgroundColor: isDarkMode ? theme.primary : theme.border },
              ]}
              onPress={toggleTheme}
            >
              <View
                style={[
                  styles.toggleThumb,
                  {
                    backgroundColor: 'white',
                    transform: `translateX(${isDarkMode ? 18 : 0}px)`,
                  },
                ]}
              />
            </TouchableOpacity>
          </View>
        </View>

        {/* About Section */}
        <View style={[styles.card, { backgroundColor: theme.surface, borderColor: theme.border }]}>
          <Text style={[styles.sectionTitle, { color: theme.text }]}>
            {t('settings.about')}
          </Text>

          {/* Privacy Policy */}
          <TouchableOpacity
            style={[styles.settingItem, styles.firstSettingItem, { borderBottomColor: theme.border }]}
            onPress={() => handleNavigate('PrivacyPolicy')}
          >
            <View style={styles.settingLeft}>
              <View style={[styles.settingIcon, { backgroundColor: theme.primaryLight }]}>
                <PrivacyTip sx={{ fontSize: 20, color: theme.primary }} />
              </View>
              <Text style={[styles.settingLabel, { color: theme.text }]}>
                {t('settings.privacyPolicy')}
              </Text>
            </View>
            <ChevronRight sx={{ fontSize: 20, color: theme.textMuted }} />
          </TouchableOpacity>

          {/* Terms of Service */}
          <TouchableOpacity
            style={[styles.settingItem, { borderBottomColor: theme.border }]}
            onPress={() => handleNavigate('TermsOfService')}
          >
            <View style={styles.settingLeft}>
              <View style={[styles.settingIcon, { backgroundColor: theme.primaryLight }]}>
                <Description sx={{ fontSize: 20, color: theme.primary }} />
              </View>
              <Text style={[styles.settingLabel, { color: theme.text }]}>
                {t('settings.termsOfService')}
              </Text>
            </View>
            <ChevronRight sx={{ fontSize: 20, color: theme.textMuted }} />
          </TouchableOpacity>

          {/* Help Center */}
          <TouchableOpacity
            style={[styles.settingItem, { borderBottomColor: theme.border }]}
            onPress={() => handleNavigate('HelpCenter')}
          >
            <View style={styles.settingLeft}>
              <View style={[styles.settingIcon, { backgroundColor: theme.primaryLight }]}>
                <Help sx={{ fontSize: 20, color: theme.primary }} />
              </View>
              <Text style={[styles.settingLabel, { color: theme.text }]}>
                {t('settings.helpCenter')}
              </Text>
            </View>
            <ChevronRight sx={{ fontSize: 20, color: theme.textMuted }} />
          </TouchableOpacity>

          {/* About App */}
          <TouchableOpacity
            style={styles.settingItem}
            onPress={() => handleNavigate('About')}
          >
            <View style={styles.settingLeft}>
              <View style={[styles.settingIcon, { backgroundColor: theme.primaryLight }]}>
                <Info sx={{ fontSize: 20, color: theme.primary }} />
              </View>
              <Text style={[styles.settingLabel, { color: theme.text }]}>
                {t('settings.aboutApp')}
              </Text>
            </View>
            <ChevronRight sx={{ fontSize: 20, color: theme.textMuted }} />
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Language Modal */}
      <LanguageModal
        visible={languageModalVisible}
        onClose={() => setLanguageModalVisible(false)}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
    maxWidth: 800,
    width: '100%',
    margin: '0 auto',
  },
  header: {
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    flex: 1,
    textAlign: 'center',
  },
  card: {
    marginHorizontal: 24,
    marginBottom: 24,
    padding: 24,
    borderRadius: 16,
    borderWidth: 1,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 0,
  },
  settingItem: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 20,
    marginBottom: 16,
    borderBottomWidth: 1,
  },
  firstSettingItem: {
    marginTop: 32,
  },
  settingLeft: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  settingIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  settingLabel: {
    fontSize: 16,
    fontWeight: '500',
  },
  toggle: {
    width: 44,
    height: 24,
    borderRadius: 12,
    padding: 2,
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
  },
  toggleThumb: {
    width: 20,
    height: 20,
    borderRadius: 10,
    transition: 'transform 0.2s ease',
    position: 'absolute',
    left: 2,
  },
});

export default SettingsScreen;
